// Modality ViewModel - selection state, derived UI props, and dispatchers
// for the onboarding modality picker. Selection is persisted as an onboarding
// response (questionId 'modality-categories'), independent of Creator.keywords.

import { useCallback, useMemo, useState } from 'react';
import { Actions, Dispatchers } from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import {
  MODALITY_SELECTION_LIMIT,
  MODALITY_TAG_LABELS,
  PRIMARY_MODALITY_TAG_LABELS,
} from './modalityTags';

// Frontend-defined questionId under which the modality selection is stored as
// a generic onboarding response (textValue = JSON array of selected labels).
export const MODALITY_QUESTION_ID = 'modality-categories';

// Parse a stored modality response textValue back into an ordered selection.
// Tolerates malformed/empty values by returning an empty selection.
export const parseModalitySelection = (
  textValue: string | null | undefined,
): readonly string[] => {
  if (!textValue) return [];
  try {
    const parsed = JSON.parse(textValue);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
};

type ModalityActionMap = {
  ToggleChip: string;
  Search: string;
  ToggleShowAll: null;
};

type ModalityState = {
  selection: readonly string[];
  search: string;
  showAll: boolean;
  isAtLimit: boolean;
  visibleChips: readonly string[];
  firstSelected: string | null;
  canContinue: boolean;
  counterLabel: string;
  progressPercent: number;
  hasSearchQuery: boolean;
  totalChipCount: number;
  // Whether the list has tags beyond the always-visible primary tier, i.e.
  // whether the "show all / show fewer" toggle is meaningful.
  hasCollapsibleChips: boolean;
};

type ModalityVM = {
  state: ModalityState;
} & Dispatchers<ModalityActionMap>;

export const toggleSelection = (
  selection: readonly string[],
  keyword: string,
): readonly string[] => {
  const index = selection.indexOf(keyword);
  if (index >= 0) {
    return [...selection.slice(0, index), ...selection.slice(index + 1)];
  }
  if (selection.length >= MODALITY_SELECTION_LIMIT) {
    return selection;
  }
  return [...selection, keyword];
};

const matchesSearch = (label: string, query: string): boolean => {
  if (!query.trim()) return true;
  return label.toLowerCase().includes(query.toLowerCase());
};

// Collapsed (no search): show only the primary-tier tags. Expanded or while
// searching: match against the full ordered tag list.
export const computeVisibleChips = (
  search: string,
  showAll: boolean,
): readonly string[] => {
  const hasSearchQuery = search.trim().length > 0;
  if (hasSearchQuery) {
    return MODALITY_TAG_LABELS.filter((label) => matchesSearch(label, search));
  }
  return showAll ? MODALITY_TAG_LABELS : PRIMARY_MODALITY_TAG_LABELS;
};

export const useModalityViewModel = (
  initialKeywords: readonly string[],
): ModalityVM => {
  const [selection, setSelection] =
    useState<readonly string[]>(initialKeywords);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const visibleChips = useMemo(
    () => computeVisibleChips(search, showAll),
    [search, showAll],
  );

  const _dispatch = useCallback((action: Actions<ModalityActionMap>) => {
    switch (action.type) {
      case 'ToggleChip':
        setSelection((prev) => toggleSelection(prev, action.payload));
        return;
      case 'Search':
        setSearch(action.payload);
        return;
      case 'ToggleShowAll':
        setShowAll((prev) => !prev);
        return;
    }
  }, []);

  const dispatchers = useMemo(
    () => getDispatchers<ModalityActionMap>(_dispatch),
    [_dispatch],
  );

  const isAtLimit = selection.length >= MODALITY_SELECTION_LIMIT;
  const firstSelected = selection[0] ?? null;
  const canContinue = selection.length >= 1;
  const counterLabel = `${selection.length} of ${MODALITY_SELECTION_LIMIT} selected`;
  const progressPercent = Math.min(
    100,
    (selection.length / MODALITY_SELECTION_LIMIT) * 100,
  );
  const hasSearchQuery = search.trim().length > 0;
  const totalChipCount = MODALITY_TAG_LABELS.length;
  const hasCollapsibleChips =
    MODALITY_TAG_LABELS.length > PRIMARY_MODALITY_TAG_LABELS.length;

  return {
    state: {
      selection,
      search,
      showAll,
      isAtLimit,
      visibleChips,
      firstSelected,
      canContinue,
      counterLabel,
      progressPercent,
      hasSearchQuery,
      totalChipCount,
      hasCollapsibleChips,
    },
    ...dispatchers,
  };
};
